/**
 * Product Card Hover Size Selector
 * Fixed: Real-time cart update for Minimog theme
 */

(function () {
  'use strict';

  const CART_ADD_URL = '/cart/add.js';

  // ── Feedback helper ──────────────────────────────────────────
  function showFeedback(el, message, isError = false) {
    el.textContent = message;
    el.classList.toggle('is-error', isError);
    clearTimeout(el._timer);
    el._timer = setTimeout(() => {
      el.textContent = '';
      el.classList.remove('is-error');
    }, 2500);
  }

  // ── Add to cart via fetch ─────────────────────────────────────
  async function addToCart(variantId, quantity = 1) {
    const res = await fetch(CART_ADD_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: Number(variantId), quantity })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || 'Cart add failed');
    return data;
  }

  // ── Get latest cart state ─────────────────────────────────────
  async function fetchCart() {
    const res = await fetch('/cart.js', {
      headers: { 'Accept': 'application/json' }
    });
    return res.json();
  }

  // ── Update every cart count bubble in DOM ────────────────────
  function updateCartBubble(count) {
    const selectors = [
      '[data-cart-count]',
      '.m-cart-count',
      '.cart-count',
      '[data-cart-item-count]',
      '.m-header__cart-count',
      '.m-badge',
      '.js-cart-count'
    ];
    document.querySelectorAll(selectors.join(',')).forEach(el => {
      el.textContent = count;
      // show/hide badge based on count
      if (count > 0) {
        el.removeAttribute('hidden');
        el.style.display = '';
      }
    });
  }

  // ── Trigger Minimog cart drawer open ─────────────────────────
  function openCartDrawer() {
    // Method 1: Minimog custom element
    const cartDrawer = document.querySelector('m-cart-drawer, [data-cart-drawer]');
    if (cartDrawer) {
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
        return;
      }
      cartDrawer.classList.add('is-open', 'open');
    }

    // Method 2: Minimog global theme object
    const theme = window.MinimogTheme || window.Minimog || window.theme;
    if (theme?.cartDrawer?.open) {
      theme.cartDrawer.open();
      return;
    }

    // Method 3: Dispatch events that Minimog listens to
    const events = [
      'cart:open',
      'minimog:cart:open',
      'theme:cart:open',
      'cart-drawer:open'
    ];
    events.forEach(name => {
      document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
    });

    // Method 4: Click the cart icon trigger (fallback)
    const cartToggle = document.querySelector(
      '[data-open-cart], [data-cart-toggle], .m-cart-toggle, .js-cart-toggle, [data-drawer-trigger="cart"]'
    );
    if (cartToggle) cartToggle.click();
  }

  // ── Re-render cart drawer contents via Minimog section render ─
  async function refreshCartDrawer(cart) {
    // Dispatch with full cart data — Minimog listens for this
    const cartEvents = [
      'cart:refresh',
      'minimog:cart:refresh',
      'cart:updated',
      'theme:cart:refresh'
    ];
    cartEvents.forEach(name => {
      document.dispatchEvent(new CustomEvent(name, {
        bubbles: true,
        detail: { cart }
      }));
    });

    // Try Minimog section rendering to refresh cart drawer HTML
    const cartSection = document.querySelector('[data-section-type="cart-drawer"], m-cart-drawer');
    if (cartSection) {
      const sectionId = cartSection.dataset.sectionId || cartSection.getAttribute('data-section-id');
      if (sectionId) {
        try {
          const url = `${window.location.pathname}?section_id=${sectionId}`;
          const res = await fetch(url, { headers: { 'Accept': 'text/html' } });
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newContent = doc.querySelector(
            '[data-section-type="cart-drawer"], m-cart-drawer'
          );
          if (newContent && cartSection) {
            cartSection.innerHTML = newContent.innerHTML;
          }
        } catch (e) {
          // Section render failed, events already dispatched
        }
      }
    }

    // Update bubble count
    updateCartBubble(cart.item_count);
  }

  // ── Main click handler ────────────────────────────────────────
  async function handleSizeClick(e) {
    e.preventDefault();
    e.stopPropagation();

    const btn = e.currentTarget;
    const isAvailable = btn.dataset.available === 'true';
    const variantId = btn.dataset.variantId;
    const sizesWrapper = btn.closest('.m-product-card__hover-sizes');
    const feedback = sizesWrapper.querySelector('.m-pcard-size-feedback');

    // Sold out check
    if (!isAvailable) {
      showFeedback(feedback, window.pCardSizeStrings?.soldOut || 'Sold out', true);
      return;
    }

    // Prevent double-click
    if (btn.classList.contains('is-loading')) return;

    // Mark selected
    sizesWrapper.querySelectorAll('.m-pcard-size-btn')
      .forEach(b => b.classList.remove('is-selected'));
    btn.classList.add('is-selected', 'is-loading');
    btn.setAttribute('aria-busy', 'true');

    try {
      // Step 1: Add item to cart
      await addToCart(variantId);

      // Step 2: Fetch updated cart
      const cart = await fetchCart();

      // Step 3: Update DOM immediately
      updateCartBubble(cart.item_count);

      // Step 4: Refresh drawer + dispatch events
      await refreshCartDrawer(cart);

      // Step 5: Show success feedback
      showFeedback(
        feedback,
        window.pCardSizeStrings?.added || '✓ Added to cart'
      );

      // Step 6: Open cart drawer after short delay
      setTimeout(() => openCartDrawer(), 150);

    } catch (err) {
      console.error('[SizeBtn] Cart error:', err);
      showFeedback(
        feedback,
        window.pCardSizeStrings?.error || 'Something went wrong',
        true
      );
      btn.classList.remove('is-selected');
    } finally {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
    }
  }

  // ── Bind buttons ──────────────────────────────────────────────
  function initSizeButtons() {
    document.querySelectorAll('.m-pcard-size-btn:not([data-init])').forEach(btn => {
      btn.setAttribute('data-init', '1');
      btn.addEventListener('click', handleSizeClick);
    });
  }

  // ── Init ──────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSizeButtons);
  } else {
    initSizeButtons();
  }

  // Re-init after dynamic content loads
  document.addEventListener('shopify:section:load', initSizeButtons);
  document.addEventListener('minimog:products:loaded', initSizeButtons);

  // MutationObserver for infinite scroll / filter results
  const observer = new MutationObserver(initSizeButtons);
  observer.observe(document.body, { childList: true, subtree: true });

})();